import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import { Link } from 'gatsby';
import '../design/design-guidelines-styles.css';
import { DocsHeading } from '../../components/DocsHeading';
import { Button, Paragraph, Heading } from 'react-magma-dom';
export const _frontmatter = {
  "pageTitle": "Typography Design Guidelines",
  "title": "Typography",
  "order": 2
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

<DocsHeading type="design" mdxType="DocsHeading">Typography</DocsHeading>
<p>{`Cengage applications need clear and consistent headings, highly legible body paragraphs, and clear labels. Our typefaces are chosen for legibility and maximum support for mathematical characters.`}</p>
<p>{`By consistently tying typographic styles to appropriate functions in the interface, we create a clear visual pattern for students and faculty to follow while they’re interacting with our products.`}</p>
<hr></hr>
<h2>{`Magma Type Sets`}</h2>
<p>{`Magma has three sets of ready-made typographic elements that you can choose from and should be adhered to when at all possible. Each one combines a font family with calibrated sizes and weights to work within applications or editorial content. The three sets are called `}<strong parentName="p">{`Productive`}</strong>{`, `}<strong parentName="p">{`Expressive`}</strong>{`, and `}<strong parentName="p">{`Narrative`}</strong>{`.`}</p>
<p><em parentName="p"><strong parentName="em">{`IMPORTANT:`}</strong>{` The Productive set is used by default in the components so you won't actually see it referred to in the code. You can easily change to Expressive or Narrative when appropriate by adding the necessary prop.`}</em></p>
<h3>{`Productive Type Set (Default)`}</h3>
<p>{`Productive type is reserved for use in web-based product design, where the user needs to focus on tasks. The Productive styles are curated to create a series of clear user expectations about hierarchy. The difference between the Productive and Expressive styles is mainly evident in the Headings.`}</p>
<h4>{`Productive Body Text Styles`}</h4>
<div className="type-details-table">
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="bodyLarge" mdxType="Paragraph">
        This is a good size for higher emphasis text, as well as the subheadline
        of a page.
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Body / Large</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Body / Large</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 18px / 1.125em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="bodyMedium" mdxType="Paragraph">
        This is commonly used in layouts for paragraphs. The line height and
        size make for comfortable, long-form reading, in mediums that allow for
        more space. This size type is the basis for how all other text is
        calculated.
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Body / Medium</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 16px / 1em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="bodySmall" mdxType="Paragraph">
        This is commonly used for helper and error text on inputs, as well as
        other explanatory text.
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Body / Small</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 14px / .875em</p>
      <p>Line-height: 20px / 1.25em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: .16px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="bodyXSmall" mdxType="Paragraph">
        This is good to use for captions and other meta-data or explanatory
        text.
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Body / X-Small</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 12px / .75em</p>
      <p>Line-height: 16px / 1em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: .32px</p>
    </div>
  </div>
</div>
<span className="horizontal-spacer"></span>
<h4>{`Productive Heading Text Styles`}</h4>
<div className="type-details-table">
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="headingXLarge" mdxType="Paragraph">
        This is used for layout headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / X-Large</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 36px / 2.25em</p>
      <p>Line-height: 48px / 3em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / X-Large</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 28px / 1.75em</p>
      <p>Line-height: 40px / 2.5em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="headingLarge" mdxType="Paragraph">
        This is used for layout headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Large</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 28px / 1.75em</p>
      <p>Line-height: 40px / 2.5em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Large</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 24px / 1.5em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="headingMedium" mdxType="Paragraph">
        This is used for layout headings and component titles
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Medium</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 24px / 1.5em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Medium</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="headingSmall" mdxType="Paragraph">
        This is used for layout headings and component titles
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Small</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Small</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 18px / 1.125em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="headingXSmall" mdxType="Paragraph">
        This is used for layout headings and component titles
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Heading / X-Small</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 18px / 1.125em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="heading2XSmall" mdxType="Paragraph">
        This is used for layout headings and component titles
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Heading / 2X-Small</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 12px / .75em</p>
      <p>Line-height: 16px / 1em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: .32px</p>
    </div>
  </div>
</div>
<span className="horizontal-spacer"></span>
<h3>{`Expressive Type Set`}</h3>
<p>{`Expressive type is reserved for use in editorial and digital marketing experiences which may require more dynamic typographic hierarchies and more variety in the Heading scale. The Expressive styles are curated to create a series of clear user expectations while still allowing for a more dramatic, graphic use of type.`}</p>
<h4>{`Expressive Body Text Styles`}</h4>
<div className="type-details-table">
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="expressive" noMargins visualStyle="bodyLarge" mdxType="Paragraph">
        This is a good size for higher emphasis text, as well as the subheadline
        of a page.
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Body / Large</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 24px / 1.5em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: 0px</p>
      <p>Color: foundation02 / #003865</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Body / Large</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: 0px</p>
      <p>Color: foundation02 / #003865</p>
    </div>
  </div>
</div>
<span className="horizontal-spacer"></span>
<h4>{`Expressive Heading Text Styles`}</h4>
<div className="type-details-table">
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="expressive" noMargins visualStyle="headingXLarge" mdxType="Paragraph">
        This is used for layout headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / X-Large</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 48px / 3em</p>
      <p>Line-height: 56px / 3.5em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / X-Large</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 32px / 2em</p>
      <p>Line-height: 40px / 2.5em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="expressive" noMargins visualStyle="headingLarge" mdxType="Paragraph">
        This is used for layout headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Large</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 36px / 2.25em</p>
      <p>Line-height: 48px / 3em</p>
      <p>Weight: Light / 300</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Large</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 28px / 1.75em</p>
      <p>Line-height: 40px / 2.5em</p>
      <p>Weight: Light / 300</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="expressive" noMargins visualStyle="headingMedium" mdxType="Paragraph">
        This is used for layout headings and component titles
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Medium</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 28px / 1.75em</p>
      <p>Line-height: 40px / 2.5em</p>
      <p>Weight: Light / 300</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Medium</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 24px / 1.5em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Light / 300</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="expressive" noMargins visualStyle="headingSmall" mdxType="Paragraph">
        This is used for layout headings and component titles
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Small</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 24px / 1.5em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Light / 300</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Small</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 20px / 1.5em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Light / 300</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="expressive" noMargins visualStyle="headingXSmall" mdxType="Paragraph">
        This is used for layout headings and component titles
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / X-Small</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Light / 300</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / X-Small</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 18px / 1.25em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Light / 300</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="expressive" noMargins visualStyle="heading2XSmall" mdxType="Paragraph">
        This is used for layout headings and component titles
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Heading / 2X-Small</strong>
      </p>
      <p>Typeface: Open Sans</p>
      <p>Size: 16px / 1em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: .32px</p>
    </div>
  </div>
</div>
<span className="horizontal-spacer"></span>
<h3>{`Narrative Type Set`}</h3>
<p>{`Narrative type uses Noto Serif but is otherwise styled the same as the default Productive variant. The Narrative styles are curated to create a series of clear user expectations about hierarchy, but in a font that is more pleasurable for reading long passages of text.`}</p>
<h4>{`Narrative Body Text Styles`}</h4>
<div className="type-details-table">
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="bodyLarge" mdxType="Paragraph">
        This is a good size for higher emphasis text, as well as the subheadline
        of a page.
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Body / Large</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Body / Large</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 18px / 1.125em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="bodyMedium" mdxType="Paragraph">
        This is commonly used in layouts for paragraphs. The line height and
        size make for comfortable, long-form reading, in mediums that allow for
        more space. This size type is the basis for how all other text is
        calculated.
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Body / Medium</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 16px / 1em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="bodySmall" mdxType="Paragraph">
        This size is great for creating contrast between the main paragraph of
        the passage and secondary information.
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Body / Small</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 14px / .875em</p>
      <p>Line-height: 20px / 1.25em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: .16px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="bodyXSmall" mdxType="Paragraph">
        This is good to use for captions and other meta-data or explanatory
        text.
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Body / X-Small</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 12px / .75em</p>
      <p>Line-height: 16px / 1em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: .32px</p>
    </div>
  </div>
</div>
<span className="horizontal-spacer"></span>
<h4>{`Narrative Heading Text Styles`}</h4>
<div className="type-details-table">
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="headingXLarge" mdxType="Paragraph">
        This is commonly used for H1 headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / X-Large</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 36px / 2.25em</p>
      <p>Line-height: 48px / 3em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / X-Large</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 28px / 1.75em</p>
      <p>Line-height: 40px / 2.5em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="headingLarge" mdxType="Paragraph">
        This is commonly used for H2 headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Large</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 28px / 1.75em</p>
      <p>Line-height: 40px / 2.5em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Large</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 24px / 1.5em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="headingMedium" mdxType="Paragraph">
        This is commonly used for H3 headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Medium</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 24px / 1.5em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Medium</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="headingSmall" mdxType="Paragraph">
        This is commonly used for H4 headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Small</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Small</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 18px / 1.125em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="headingXSmall" mdxType="Paragraph">
        This is commonly used for H5 headings
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Heading / X-Small</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 18px / 1.125em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="heading2XSmall" mdxType="Paragraph">
        This is commonly used for H6 headings
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Heading / 2X-Small</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 12px / .75em</p>
      <p>Line-height: 16px / 1em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: .32px</p>
    </div>
  </div>
</div>
<span className="horizontal-spacer"></span>
<h3>{`Changing Headings Styles`}</h3>
<p>{`The headings presets above are assigned by default to H1 through H6 in the `}<Link to="/api/heading/" mdxType="Link">{`Headings component`}</Link>{`. But it's relatively common to need a specific heading type in the markup, but not want the default styling. For this reason we have made it very easy to change which styling preset you use in the headings component.`}</p>
<p>{`One example of this is in the `}<Link to="/api/modal/" mdxType="Link">{`Modal component`}</Link>{`. For optimal accessibility the title of the modal needs to be an H1, but we definitely don't want it to be styled with text that large. So we use an H1, but apply the Productive Heading Small style to it. This flexibility allows consumers of Magma to design using the styles that work best for the UI, but still have a clear informational hierarchy for assistive technologies.`}</p>
<figure>{`
  `}<span parentName="figure" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1035px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "43.62934362934363%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAJCAYAAAAywQxIAAAACXBIWXMAAAsTAAALEwEAmpwYAAABkElEQVQoz52Pu0oDQRSGt7H3ASwsxMLeQgRfQMSAEFMkIJhswEZQrHwBGxsRQUFtrXN5iiSQaAqFZFfjFrpJzJKN2euc35lZVvGCggc+ZubMf875j6Lmciyfz7+jqirLZbMRm9mP+9ccr4uRNfzM8rxycHIOrdXC9c0Nms0mbu9u0dZ1tO91aI8PPyL+WrwmRtM0FAoFZDIZKAfnV0SBT71en7r9Hg3NPsENCWOf2NOA2LNFzLSiU74HBMcnAJ9oNBqUTqdJOTq9BLEQpmliaA/BPB+B58HYOMTDZAqd6U10pjYkjzMq9IkEjK1jMN4l5Frf9yGiXq9HDkulEogIo9EI4/EYnuNCSDp7Z2jP5aEv7UGb34a2sAN9cRet2Rw6+xdSI7Su68qGtVoN3GHUkDEGy7LgcWdhGEocewS72+e8fMN5fZWaIAjeHYqGyWQSSrlcls4Mw4Bt2/JTDPgrxFYC0VhEtVrFamIVSrFYlAnHcaRDMTWe/BuxRtSIqFQqSK2nopXjif+JuE6svLy2gjeUJ2gC2aGRYQAAAABJRU5ErkJggg==')",
            "backgroundSize": "cover",
            "display": "block"
          }
        }}></span>{`
  `}<img parentName="span" {...{
          "className": "gatsby-resp-image-image",
          "alt": " ",
          "title": " ",
          "src": "/version/2.6.2/static/975193ffa5ee09faedc99ac90ee317b0/e3189/modal-title-style.png",
          "srcSet": ["/version/2.6.2/static/975193ffa5ee09faedc99ac90ee317b0/a2ead/modal-title-style.png 259w", "/version/2.6.2/static/975193ffa5ee09faedc99ac90ee317b0/6b9fd/modal-title-style.png 518w", "/version/2.6.2/static/975193ffa5ee09faedc99ac90ee317b0/e3189/modal-title-style.png 1035w", "/version/2.6.2/static/975193ffa5ee09faedc99ac90ee317b0/4352a/modal-title-style.png 1364w"],
          "sizes": "(max-width: 1035px) 100vw, 1035px",
          "style": {
            "width": "100%",
            "height": "100%",
            "margin": "0",
            "verticalAlign": "middle",
            "position": "absolute",
            "top": "0",
            "left": "0"
          },
          "loading": "lazy"
        }}></img>{`
    `}</span>
</figure>
<h4>{`Consistency is Key`}</h4>
<p>{`Continuing from above, there isn't just one way an H1 or and H3 should be styled. We have chosen logical default styles, but it's easy to say you need an H3 to be styled using one of the larger or smaller presets. The key is to be consistent. If you style a page title using productive-heading-large, then you need to use the same styling on all of the pages. If you use "productive-heading-xsmall" to style the title of a section, then you should use the same styling on equivalent sections.`}</p>
<h3>{`Responsive Behavior`}</h3>
<p>{`Another advantage to using the presets above is that Responsive behavior is already built into each style so you don't have to figure out and include it yourself. This means a number of the larger sizes will automatically reduce in size at 600px wide. This ensures we have appropriate text sizes on smaller devices. The breakpoint can be overridden if you want this change to happen earlier or later.`}</p>
<figure>{`
  `}<span parentName="figure" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1035px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "47.87644787644788%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAKCAIAAAA7N+mxAAAACXBIWXMAAAsTAAALEwEAmpwYAAABHUlEQVQoz5WRyW6DMBCGeV2EhERE73kqKNzhQBAKQgICZWtZcmgCFVCWpsuPSSM4tGq/g2WPPDPf2FTXda+E2+bvUMMwvBPGcbxcLm8Lxl/BBQoNXdcNgiCO4zRNP/8DhQJlWZ7PZ2jUdQ2Rvu9RGEdFUQRBuCdgI4qiJEmyLGNvWdbUuW3bRwI6J0mCNYqiLMtUVd1sNncEnudpmmYYBhGO41iW3W638J2SozVhGKLWbrc7HA7H4zHP86IoBgKkYATh/X6v6/qPyYZhQAQKzwS86AcBj4pkx3GuyQ9rfN9HCXRGFfR8IsTfzEHTNDVNm77qZU1VVU3ToIrnefNtcPPCv2AQPBiC1KyxBG5YMS0U4L/MnIfCLLZtn06nL9EnFjTNI+J5AAAAAElFTkSuQmCC')",
            "backgroundSize": "cover",
            "display": "block"
          }
        }}></span>{`
  `}<img parentName="span" {...{
          "className": "gatsby-resp-image-image",
          "alt": " ",
          "title": " ",
          "src": "/version/2.6.2/static/eee6d869ae16d06d320062783d5d340f/e3189/responsive-example.png",
          "srcSet": ["/version/2.6.2/static/eee6d869ae16d06d320062783d5d340f/a2ead/responsive-example.png 259w", "/version/2.6.2/static/eee6d869ae16d06d320062783d5d340f/6b9fd/responsive-example.png 518w", "/version/2.6.2/static/eee6d869ae16d06d320062783d5d340f/e3189/responsive-example.png 1035w", "/version/2.6.2/static/eee6d869ae16d06d320062783d5d340f/f793b/responsive-example.png 1404w"],
          "sizes": "(max-width: 1035px) 100vw, 1035px",
          "style": {
            "width": "100%",
            "height": "100%",
            "margin": "0",
            "verticalAlign": "middle",
            "position": "absolute",
            "top": "0",
            "left": "0"
          },
          "loading": "lazy"
        }}></img>{`
    `}</span>
</figure>
<hr></hr>
<h2>{`Typefaces`}</h2>
<h3>{`Open Sans`}</h3>
<p>{`Our primary typface is Open Sans. It’s clear, approachable and has a great mathematical glyph set. It is great for headlines, subheads, navigation and body copy.`}</p>
<undefined><a {...{
        "href": "https://fonts.google.com/specimen/Open+Sans",
        "target": "_blank"
      }}>{`
  Download Open Sans
`}</a>
<div {...{
        "className": "two-col"
      }}>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "66.02316602316603%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsTAAALEwEAmpwYAAABnElEQVQ4y41TOcsCUQzcnyt2/gFBbLUQBZsFKwtBO0EUGxFsFKwsFrzvCxXv9RqZQBbX7xMVhrzkJeO8JGucTiccj0fQzudz50yrOBwOrhjPn2Aw4XK5YLFYwOv1wrIs3O93ubxer7BtW/xvRA7hfr8Hf8ViEcFgEOl0WvzlcolUKoVYLIZSqYTb7SZKf1J4Pp8RCoXQbrcRDodFLVV3u13UajX4/X7sdjvJ+0r4eDzQaDTg8XiQSCTEVioVtFotmKaJTCaDQCCAzWbzGyGfF41GUS6XQfJ6vY5IJCKK8/k8BoMBfD6fi/A/6NCM7XaLbDYr/eEA+NRCoYBqtYp4PI5kMolcLgfmkfCTMiWVHmrDOSCC02WM5LScMu81R4ejK+VSyACb3+v10Ol0MBwOxSf6/b4MiuDTm82m3E8mE8lln8fj8V9CBmezmVgW0rKQlhiNRphOp04eCfkRvKtz9pDFLKBKVUbi9XotK0SS1Wolu0mQjL721KWQhCxgIQlJrArYQw6KYLGe1X9dIe2pQUf/SRMVn1bkfVVeiZ9jo8dOojh6UAAAAABJRU5ErkJggg==')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": " ",
                  "title": " ",
                  "src": "/version/2.6.2/static/a930b5249d87fe089d3818dc1d0c743e/8ae3e/open-sans-characters.png",
                  "srcSet": ["/version/2.6.2/static/a930b5249d87fe089d3818dc1d0c743e/a2ead/open-sans-characters.png 259w", "/version/2.6.2/static/a930b5249d87fe089d3818dc1d0c743e/6b9fd/open-sans-characters.png 518w", "/version/2.6.2/static/a930b5249d87fe089d3818dc1d0c743e/8ae3e/open-sans-characters.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col col-2"
          }}>{`
      Open Sans is a humanist sans serif typeface designed by Steve Matteson,
      Type Director of Ascender Corp. This version contains the complete 897
      character set, which includes the standard ISO Latin 1, Latin CE, Greek
      and Cyrillic character sets. Open Sans was designed with an upright
      stress, open forms and a neutral, yet friendly appearance. It was
      optimized for print, web, and mobile interfaces, and has excellent
      legibility characteristics in its letterforms.
    `}</div>{`
  `}</div>
</div></undefined>
<h3>{`Noto Serif`}</h3>
<p>{`Noto Serif is a beautiful serif font. It's easy to read, provides a nice contrast with Open Sans and has great support for special characters. At this point, Noto Serif should primarily be used for the reader applications that display our text books.`}</p>
<undefined><a {...{
        "href": "https://fonts.google.com/specimen/Noto+Serif",
        "target": "_blank"
      }}>{`
  Download Noto Serif
`}</a>
<div {...{
        "className": "two-col"
      }}>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "66.02316602316603%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAABcUlEQVQoz32Sy4rCYAyF+7r6FuIFXOnWjSsVCqLoxhuIrnXnQooy9VrBFkGxVev188+MDog9i5A/OSdJk2oHhdPpZNv2drs9Ho88Xdf1PE/s4Ts0od7v90gkks/ncfb7/fV6JX4+nykaoNfIXS6X+XyeSqXQE0LZarWy2WyhUHAcJ0CvSdtKpTIYDGKxWL/f59ntdi3Lolyj0eAJ5+vY1E6n07VaLRqNZjIZ2OVyud1ux+PxZrMZJCbX6XSYEGc4HIZCofF4HA6HGTiRSJRKpSAxbXVdLxaLrMcwjGQy2ev16vV6LperVquMTfzrN5OQrboKbItrsUIit9sNX+Kc4PN+T/FkMjFNE8vOmXk2m2F/FCQ1Go2WyyVZU2GxWMgJntsmsVqtWO90OsUifjmkLAUI+LvdjobvzswDSag0REYHWwHBer3ebDaOAj5k+QXfYpRQpQQCeHyz7/vsAsuE/h9E+dL/3lmsgJz3D68+nzt/ADlpwZedqoUBAAAAAElFTkSuQmCC')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": " ",
                  "title": " ",
                  "src": "/version/2.6.2/static/f2a309f68dfda25e4170e7c5bb7adbd9/8ae3e/noto-serif-characters.png",
                  "srcSet": ["/version/2.6.2/static/f2a309f68dfda25e4170e7c5bb7adbd9/a2ead/noto-serif-characters.png 259w", "/version/2.6.2/static/f2a309f68dfda25e4170e7c5bb7adbd9/6b9fd/noto-serif-characters.png 518w", "/version/2.6.2/static/f2a309f68dfda25e4170e7c5bb7adbd9/8ae3e/noto-serif-characters.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col col-2"
          }}>{`
      `}<p parentName="div">{`
        When text is rendered by a computer, sometimes there will be characters
        in the text that can not be displayed, because no font that supports
        them is available to the computer. When this occurs, small boxes are
        shown to represent the characters. We call those small boxes “tofu,” and
        we want to remove tofu from the Web. This is how the Noto font families
        got their name.
      `}</p>{`
      `}<p parentName="div">{`
        Noto helps to make the web more beautiful across platforms for all
        languages. Currently, Noto covers over 30 scripts, and will cover all of
        Unicode in the future. This is the Serif Latin, Greek and Cyrillic
        family. It has Regular, Bold, Italic and Bold Italic styles and is
        hinted.
      `}</p>{`
    `}</div>{`
  `}</div>
</div></undefined>
<hr></hr>
<h2>{`Type Scale`}</h2>
<p>{`The font sizes used in the sets above come from the full type scale. This type scale is based on the Major Second Type Scale (1.125) but is slightly modified to round to whole numbers and create a simple pattern in the difference between each size. Small font sizes differ by 2px, and then that difference increases by 4px and then 6px as the size gets larger.`}</p>
<p>{`Line-heights are also defined at this level to adhere to an 8px spacing system, and should not be changed unless absolutely necessary`}</p>
<p>{`If you have a need to use a font size outside of the pre-made sets above, you may only choose from this list.`}</p>
<figure>{`
  `}<span parentName="figure" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1035px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "122.00772200772201%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAYCAIAAAB1KUohAAAACXBIWXMAAAsTAAALEwEAmpwYAAAByklEQVQ4y5WU246CQBBE/f/fMvGBmGCM0dUHr6iAV1BR8bZnLQPLAEYncdIzdHVVNS2Vx5frfr8ncYXfer2+3W7b7TaOYz0uW6T5vj+dTufz+QscRRG3p9Npt9uxE7/BX6/X/X5PTgrmCtrD4QD4v7D8AkZ+Kltqj8djEARUpdB7MBwp+HK5sHO12WzAU+UL8Pl8hg1MGIbYJpClsm4n1f/A+GRHMA0PnktaPvKsVMDL5RI84uEvaxv3IjM9A4YWPPrLyE0wntmB8fYZGMZgsVgQGPNUIFtzI2bP8+BcrVYUUucKPWcaJjDZMAOGGVrEF4JN2TrQp8FgwNDifDQageeYd24ya6SQjVV22k6gUWf4DNsck4oVwagHc6vV6na7yIaz3++jgqNBTibVU7AWPUQwhAihEEdoo+fKk6fdVm0Im82mbdvQTiYTqXBd17IsmpdgipnhoUlYgBl+fRuoS6r+dvn18szOe6rX6z/PRbc7nU673Yac+0ajgQQB9DFIwdLDnM1mM2zzjJhAQ06MiuTdZjwn44lmx3HI05xBxQtj2oh51Ov1NLB4Vn4GTCqya7Ua+3g8Hg6H1WpVbcMFl3RU4MyEFX5WP1m/aFlyR/L4ABQAAAAASUVORK5CYII=')",
            "backgroundSize": "cover",
            "display": "block"
          }
        }}></span>{`
  `}<img parentName="span" {...{
          "className": "gatsby-resp-image-image",
          "alt": " ",
          "title": " ",
          "src": "/version/2.6.2/static/9897d207f1910a0388b2ff52003b67b2/e3189/type-scale.png",
          "srcSet": ["/version/2.6.2/static/9897d207f1910a0388b2ff52003b67b2/a2ead/type-scale.png 259w", "/version/2.6.2/static/9897d207f1910a0388b2ff52003b67b2/6b9fd/type-scale.png 518w", "/version/2.6.2/static/9897d207f1910a0388b2ff52003b67b2/e3189/type-scale.png 1035w", "/version/2.6.2/static/9897d207f1910a0388b2ff52003b67b2/f793b/type-scale.png 1404w"],
          "sizes": "(max-width: 1035px) 100vw, 1035px",
          "style": {
            "width": "100%",
            "height": "100%",
            "margin": "0",
            "verticalAlign": "middle",
            "position": "absolute",
            "top": "0",
            "left": "0"
          },
          "loading": "lazy"
        }}></img>{`
    `}</span>
</figure>
<hr></hr>
<h2>{`Styles`}</h2>
<h3>{`Weight`}</h3>
<p>{`Font weight is an important typographic variable that can add emphasis and differentiate content hierarchy. Font weight and size pairings must be carefully balanced. A bold weight will always have more emphasis than a lighter weight font of the same size. However, a lighter weight font can rank hierarchically higher than a bold font if the lighter weight type size is significantly larger than the bold one. The weights available in React Magma are `}<inlineCode parentName="p">{`light (300)`}</inlineCode>{`, `}<inlineCode parentName="p">{`regular (400)`}</inlineCode>{`, `}<inlineCode parentName="p">{`semi-bold (600)`}</inlineCode>{` and `}<inlineCode parentName="p">{`bold (700)`}</inlineCode>{`.`}</p>
<div {...{
      "className": "two-col"
    }}>{`
  `}<div parentName="div" {...{
        "className": "row"
      }}>{`
    `}<div parentName="div" {...{
          "className": "col col-1"
        }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
              "className": "gatsby-resp-image-wrapper",
              "style": {
                "position": "relative",
                "display": "block",
                "marginLeft": "auto",
                "marginRight": "auto",
                "maxWidth": "756px"
              }
            }}>{`
      `}<span parentName="span" {...{
                "className": "gatsby-resp-image-background-image",
                "style": {
                  "paddingBottom": "66.02316602316603%",
                  "position": "relative",
                  "bottom": "0",
                  "left": "0",
                  "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAABL0lEQVQoz4VS26qCQBT1/z9B/AXRhx4EQX0oxF7FVNIwQknzktfSo3AWDnk6lbkehj06e6/LDHV7Q9M0pLiPuM2DemmbTtd1XRRFWZYolpvbts2ybL/f27aN9Xg87nY70zRd121GLDADaZpGUURoCXNVVT8jvjVD8PV6BWGSJJfLBSPiOEZ9OBwcx/F9/90/NbmFbJxWVVWW5fV6res6ZEP/ZrPZbrfYzjZP6PseU1B0XTcMA1YIxpdvnvEP58IwlCRJ0zSwgR+E2BqGoSiKIAiiKMLF86B/npENsoVJz/MsyyL1+XxG5tjCBeKcbc7zHOSIDStyDoLgdDohueGBF9t/suEWPDRNr1YrlmUhnuM4hmF4nm8eWHhhUE7uFpdM3haEfEzrQ9rPLgjbl+f9C3hc1ojWf6gJAAAAAElFTkSuQmCC')",
                  "backgroundSize": "cover",
                  "display": "block"
                }
              }}></span>{`
  `}<img parentName="span" {...{
                "className": "gatsby-resp-image-image",
                "alt": " ",
                "title": " ",
                "src": "/version/2.6.2/static/be75833fd612fad8eded113ac6830f71/8ae3e/font-weights.png",
                "srcSet": ["/version/2.6.2/static/be75833fd612fad8eded113ac6830f71/a2ead/font-weights.png 259w", "/version/2.6.2/static/be75833fd612fad8eded113ac6830f71/6b9fd/font-weights.png 518w", "/version/2.6.2/static/be75833fd612fad8eded113ac6830f71/8ae3e/font-weights.png 756w"],
                "sizes": "(max-width: 756px) 100vw, 756px",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                },
                "loading": "lazy"
              }}></img>{`
    `}</span>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
          "className": "col col-2"
        }}></div>{`
  `}</div>
</div>
<h3>{`Italic`}</h3>
<p>{`Each weight has an italic style, which should only be used when you need to emphasize certain words in a sentence (titles of works, technical terms, names of devices, captions, etc.).`}</p>
<div {...{
      "className": "two-col"
    }}>{`
  `}<div parentName="div" {...{
        "className": "row"
      }}>{`
    `}<div parentName="div" {...{
          "className": "col col-1"
        }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
              "className": "gatsby-resp-image-wrapper",
              "style": {
                "position": "relative",
                "display": "block",
                "marginLeft": "auto",
                "marginRight": "auto",
                "maxWidth": "756px"
              }
            }}>{`
      `}<span parentName="span" {...{
                "className": "gatsby-resp-image-background-image",
                "style": {
                  "paddingBottom": "66.02316602316603%",
                  "position": "relative",
                  "bottom": "0",
                  "left": "0",
                  "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAABXklEQVQoz5WSSYvCQBCF/f93/4IE8eBFgqIXMZEBJQmu0aBixAWzuE3cEvAjDZNMRgasw+N1dVfVq6rOXVMWBMH1E8u99d5uNxKB5/P5dDpdLpf/gnm6Wq10XZ9Op8vlst/vm6a52+0mk8lgMBiPx57n/ZWWBFNkvV5/x3Y4HCgonJDj8fh4PKIoQks2mEf3+9227e12u9/vBWJ4kOP7Ph4UoYIs6fiksqZpjUaj1WoZhtHr9ebzebvd7nQ6s9mMo6IoXJERCT/ik+Dn8wnhDhKGIUgRiPAIzWKKb2RT5yu2arUKNpvNbrerqipyqD8ajer1eq1Ww8PwRYpkVQx5sVhYlsVskQqhZ5BuN5sNzUOGwyGdZ3smE/24rgt6sTmOA+eKaZEXFOJFd9lgSZIKhUK5XEZzsViEVCoVpijLcj6fL5VKjDqz6l97phmQI+8EZ09BbGz+g++Z/qdpT9peuEbNhWJkr0gAAAAASUVORK5CYII=')",
                  "backgroundSize": "cover",
                  "display": "block"
                }
              }}></span>{`
  `}<img parentName="span" {...{
                "className": "gatsby-resp-image-image",
                "alt": " ",
                "title": " ",
                "src": "/version/2.6.2/static/ecf034da4fdcc96ba00cb6ae34abfce3/8ae3e/font-italic.png",
                "srcSet": ["/version/2.6.2/static/ecf034da4fdcc96ba00cb6ae34abfce3/a2ead/font-italic.png 259w", "/version/2.6.2/static/ecf034da4fdcc96ba00cb6ae34abfce3/6b9fd/font-italic.png 518w", "/version/2.6.2/static/ecf034da4fdcc96ba00cb6ae34abfce3/8ae3e/font-italic.png 756w"],
                "sizes": "(max-width: 756px) 100vw, 756px",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                },
                "loading": "lazy"
              }}></img>{`
    `}</span>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
          "className": "col col-2"
        }}></div>{`
  `}</div>
</div>
<h3>{`Color`}</h3>
<p>{`Type color should be carefully considered, with legibility and accessibility as your main concerns. Color can be helpful for reinforcing the meaning or context of a piece of text or action, but color should never be the only way this is communicated, or just for decoration. When text is generally kept neutral in color, then it can be much more powerful when you do need to use it to point out actions or a special important messages. The text itself is the clearest way to explain what the user needs to know, but icons can also help provide helpful clues.`}</p>
<div {...{
      "className": "two-col"
    }}>{`
  `}<div parentName="div" {...{
        "className": "row"
      }}>{`
    `}<div parentName="div" {...{
          "className": "col col-1"
        }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
              "className": "gatsby-resp-image-wrapper",
              "style": {
                "position": "relative",
                "display": "block",
                "marginLeft": "auto",
                "marginRight": "auto",
                "maxWidth": "756px"
              }
            }}>{`
      `}<span parentName="span" {...{
                "className": "gatsby-resp-image-background-image",
                "style": {
                  "paddingBottom": "66.02316602316603%",
                  "position": "relative",
                  "bottom": "0",
                  "left": "0",
                  "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAA1klEQVQoz7WR6Q6CMBCEef/ng0C5oVzlRo4QLh1LIgaMiYrzo5ktzH7bVrj+IOGE8MK188tBL8LH3Q/IbdtqmkYpTZJEVVXf9w3DsCzLtm3GWBzHnuc5joMS//R9vx+bcVVVlWVZEARhGOZ5ji5pmhZFgab4ihXlOI7TNG3hrut0XSeEAIIYsLIsw7iuqygKRgA2iiLTNFFKkoSm62Hv4WEYwLxwNU0DD05ZlijBRwkUgDDAwMzzvJHrugYWpxVFEcCVgB1cBOF6zPmfd34275/3VPJ3ugHZ7vPRGTBU6wAAAABJRU5ErkJggg==')",
                  "backgroundSize": "cover",
                  "display": "block"
                }
              }}></span>{`
  `}<img parentName="span" {...{
                "className": "gatsby-resp-image-image",
                "alt": " ",
                "title": " ",
                "src": "/version/2.6.2/static/9c6faf87e0bff33ddd4e5155abd59cd0/8ae3e/text-color-neutral-1.png",
                "srcSet": ["/version/2.6.2/static/9c6faf87e0bff33ddd4e5155abd59cd0/a2ead/text-color-neutral-1.png 259w", "/version/2.6.2/static/9c6faf87e0bff33ddd4e5155abd59cd0/6b9fd/text-color-neutral-1.png 518w", "/version/2.6.2/static/9c6faf87e0bff33ddd4e5155abd59cd0/8ae3e/text-color-neutral-1.png 756w"],
                "sizes": "(max-width: 756px) 100vw, 756px",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                },
                "loading": "lazy"
              }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                "className": "title title-correct"
              }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`
            Text should generally be kept neutral in color until there's a
            reason to use a color.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
          "className": "col col-2"
        }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
              "className": "gatsby-resp-image-wrapper",
              "style": {
                "position": "relative",
                "display": "block",
                "marginLeft": "auto",
                "marginRight": "auto",
                "maxWidth": "756px"
              }
            }}>{`
      `}<span parentName="span" {...{
                "className": "gatsby-resp-image-background-image",
                "style": {
                  "paddingBottom": "66.02316602316603%",
                  "position": "relative",
                  "bottom": "0",
                  "left": "0",
                  "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAABSklEQVQoz2NgsEglH1FBM6NlGhCBGehsuCyEjaEZKGqazGCSxGCWAiIhCMI2TYJKQRCQi6LZLJnXKUc/tkkttEbat9QwrkkpuFI3ukE7sl4zok7Wr0zGt0wlpFojvFYzolYtrIbTPguoBWEzo0UqUJGcf7mwe6GUd4liYKVCQIWYZ5FSUKWUT6moZ6G0TylQFkhKepew2mQwW6XDNJun8Djl6sU0GsY3A40A6tSNaTRJbAUy1MNqTBJbgE4AWisXUK4b1QAUt0hpBzoN6BdgiIA0s9tmCrsXCLoBUT6/Sx6QDXEFUETcs0jIrYDZOp3VNgPI4HHMAdrMZJUG93OKgGu+UXwz0LeWqR0KgRU60SAbjBKaDWKbjBNagIjFOh3kTxBKAVoGQojQBnKAQQokzWAIyIUgiB5IdOKJZ2QGPLbBDIIpzByG6JY8ASzGtTnobZhhAAAAAElFTkSuQmCC')",
                  "backgroundSize": "cover",
                  "display": "block"
                }
              }}></span>{`
  `}<img parentName="span" {...{
                "className": "gatsby-resp-image-image",
                "alt": " ",
                "title": " ",
                "src": "/version/2.6.2/static/a847c547e434b11876e4f29f119748ec/8ae3e/text-color-neutral-2.png",
                "srcSet": ["/version/2.6.2/static/a847c547e434b11876e4f29f119748ec/a2ead/text-color-neutral-2.png 259w", "/version/2.6.2/static/a847c547e434b11876e4f29f119748ec/6b9fd/text-color-neutral-2.png 518w", "/version/2.6.2/static/a847c547e434b11876e4f29f119748ec/8ae3e/text-color-neutral-2.png 756w"],
                "sizes": "(max-width: 756px) 100vw, 756px",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                },
                "loading": "lazy"
              }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                "className": "title title-correct"
              }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`On dark backgrounds, neutral text appears as white.`}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
  `}</div>{`
  `}<div parentName="div" {...{
        "className": "row"
      }}>{`
    `}<div parentName="div" {...{
          "className": "col col-1"
        }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
              "className": "gatsby-resp-image-wrapper",
              "style": {
                "position": "relative",
                "display": "block",
                "marginLeft": "auto",
                "marginRight": "auto",
                "maxWidth": "756px"
              }
            }}>{`
      `}<span parentName="span" {...{
                "className": "gatsby-resp-image-background-image",
                "style": {
                  "paddingBottom": "66.02316602316603%",
                  "position": "relative",
                  "bottom": "0",
                  "left": "0",
                  "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAA30lEQVQoz71SaUsDMRDt//91Kmst1Fp60M2xOTfJxlz1raC24CerDsMwGfIOMlmcb4jFzeD2XX7MW2uXzVx/TTmaaI6GPdKxH+3JmoNGimchN9Ixx5+Y2kqz13zF5Ysc1kNJ5QucQrJHg5zUFETwwnvmHHee+6ACiGxvPXcQGMnMXl4vwDlm2lG2ZCAGBV8y8kBYR8VG6J0m973eKdoRsR5wDY3aqvlBWnsHhxTVBM9BBigjS8w1l5pqLRU6YIc7DPOUcbyyDXv93YmvGMRR9V5/GvvrPZ+v1/uPP+yn8QY5dfpZMh4OZwAAAABJRU5ErkJggg==')",
                  "backgroundSize": "cover",
                  "display": "block"
                }
              }}></span>{`
  `}<img parentName="span" {...{
                "className": "gatsby-resp-image-image",
                "alt": " ",
                "title": " ",
                "src": "/version/2.6.2/static/d961a49514c61696e5844b246a0e3a38/8ae3e/text-color-decoration.png",
                "srcSet": ["/version/2.6.2/static/d961a49514c61696e5844b246a0e3a38/a2ead/text-color-decoration.png 259w", "/version/2.6.2/static/d961a49514c61696e5844b246a0e3a38/6b9fd/text-color-decoration.png 518w", "/version/2.6.2/static/d961a49514c61696e5844b246a0e3a38/8ae3e/text-color-decoration.png 756w"],
                "sizes": "(max-width: 756px) 100vw, 756px",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                },
                "loading": "lazy"
              }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                "className": "title title-incorrect"
              }}>{`Incorrect`}</p>{`
          `}<p parentName="figcaption">{`Don't use color as decoration.`}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
          "className": "col col-2"
        }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
              "className": "gatsby-resp-image-wrapper",
              "style": {
                "position": "relative",
                "display": "block",
                "marginLeft": "auto",
                "marginRight": "auto",
                "maxWidth": "756px"
              }
            }}>{`
      `}<span parentName="span" {...{
                "className": "gatsby-resp-image-background-image",
                "style": {
                  "paddingBottom": "66.02316602316603%",
                  "position": "relative",
                  "bottom": "0",
                  "left": "0",
                  "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAyUlEQVQoz71SawsBURT0//+VREQojwi13mLtWrv3rl1n5rpCEVJsTufDeTQzp+bkzA+RywTMc5LX4r59GGarzMNG3DK8GsIB1RjhEOEI+y6CLpUDr0o9E7+JsG+IJ/BRUU2gHKop4xX1HHphGS2RbRGNeHDtFvHyFTjZYteC34DfZORYQQRt+HXZVmRTsAk1fn82EqY7xgtoq7w8i6eBQWIkNkiNaHOM3oItsazz8OriFhF0xC1h37tZ8NEqXvz46UkeTf7bh30TJy84/oQxkJ7fAAAAAElFTkSuQmCC')",
                  "backgroundSize": "cover",
                  "display": "block"
                }
              }}></span>{`
  `}<img parentName="span" {...{
                "className": "gatsby-resp-image-image",
                "alt": " ",
                "title": " ",
                "src": "/version/2.6.2/static/544e1c7d79f2d1d49b986795ea47795f/8ae3e/text-color-contrast.png",
                "srcSet": ["/version/2.6.2/static/544e1c7d79f2d1d49b986795ea47795f/a2ead/text-color-contrast.png 259w", "/version/2.6.2/static/544e1c7d79f2d1d49b986795ea47795f/6b9fd/text-color-contrast.png 518w", "/version/2.6.2/static/544e1c7d79f2d1d49b986795ea47795f/8ae3e/text-color-contrast.png 756w"],
                "sizes": "(max-width: 756px) 100vw, 756px",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                },
                "loading": "lazy"
              }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                "className": "title title-incorrect"
              }}>{`Incorrect`}</p>{`
          `}<p parentName="figcaption">{`
            The contrast between text and its background needs to be 4.5:1, or
            3:1 for large (18px) text.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
  `}</div>{`
  `}<div parentName="div" {...{
        "className": "row"
      }}>{`
    `}<div parentName="div" {...{
          "className": "col col-1"
        }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
              "className": "gatsby-resp-image-wrapper",
              "style": {
                "position": "relative",
                "display": "block",
                "marginLeft": "auto",
                "marginRight": "auto",
                "maxWidth": "756px"
              }
            }}>{`
      `}<span parentName="span" {...{
                "className": "gatsby-resp-image-background-image",
                "style": {
                  "paddingBottom": "66.02316602316603%",
                  "position": "relative",
                  "bottom": "0",
                  "left": "0",
                  "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAA9ElEQVQoz62S0W6DIBSG+6h7g13sCZplj9F32DP0rmmaLNvcagsoCEIpWq0K2ikOm63ZLrf654TAd/hzTjhM+is0GcFsre3/FONUbstqe3uXL5Ynua8jWpPIQGQwqeP4lCSOmBAPKcoaLtzqwrbtt1lrNL0vn18c1QjpLdAAGozLV++8BwYFJqIVgDVj1bvvbo7Utm6Kh8ebN7rYHxlR60htVMFFFrIDTAvBD6HMKU3ALsNAPDkyPHBvv8zNh5nNpz5fySNlKcRqnVY7ovxAeqqI3VHkhCQ+zwIkvbT8bb52zp3t7E8Ng7cD7C+ou6RG/WH/0ycTLvLsjkNywQAAAABJRU5ErkJggg==')",
                  "backgroundSize": "cover",
                  "display": "block"
                }
              }}></span>{`
  `}<img parentName="span" {...{
                "className": "gatsby-resp-image-image",
                "alt": " ",
                "title": " ",
                "src": "/version/2.6.2/static/5eefa29f8ca386c44c1c6a5ecc6bd9a5/8ae3e/text-color-meaning.png",
                "srcSet": ["/version/2.6.2/static/5eefa29f8ca386c44c1c6a5ecc6bd9a5/a2ead/text-color-meaning.png 259w", "/version/2.6.2/static/5eefa29f8ca386c44c1c6a5ecc6bd9a5/6b9fd/text-color-meaning.png 518w", "/version/2.6.2/static/5eefa29f8ca386c44c1c6a5ecc6bd9a5/8ae3e/text-color-meaning.png 756w"],
                "sizes": "(max-width: 756px) 100vw, 756px",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                },
                "loading": "lazy"
              }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                "className": "title title-correct"
              }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`
            Appropriate color usage can help reinforce the meaning of text, but
            is not a replacement for clear text and other helpful visual aids
            like icons.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
          "className": "col col-2"
        }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
              "className": "gatsby-resp-image-wrapper",
              "style": {
                "position": "relative",
                "display": "block",
                "marginLeft": "auto",
                "marginRight": "auto",
                "maxWidth": "756px"
              }
            }}>{`
      `}<span parentName="span" {...{
                "className": "gatsby-resp-image-background-image",
                "style": {
                  "paddingBottom": "66.02316602316603%",
                  "position": "relative",
                  "bottom": "0",
                  "left": "0",
                  "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAuElEQVQoz62ROw6DMBBEuTziAhyAK9FYYCHAIAPm24FckOQppkpH8BTWauTZmd0N3g8QeBC/bsKT877vYRimabosS9/3Xdc1TaO1NsZs2wZDva7rMAzTNJkvzvO8xMdxxHGcZRls27Z1XSulaCGldDUkLSjQl2WZ57m/2FEUCSFIRWB85nnGBHPS4jmOI8lhiqKAcQu+xNbaJEkI6T6hZ1ReMtOFtG4culRV9St+eme2d/fInpz/wwfnvfFJ2Tk+zAAAAABJRU5ErkJggg==')",
                  "backgroundSize": "cover",
                  "display": "block"
                }
              }}></span>{`
  `}<img parentName="span" {...{
                "className": "gatsby-resp-image-image",
                "alt": " ",
                "title": " ",
                "src": "/version/2.6.2/static/200f50aebb501fdd2ff5f564825d8634/8ae3e/text-color-meaning-2.png",
                "srcSet": ["/version/2.6.2/static/200f50aebb501fdd2ff5f564825d8634/a2ead/text-color-meaning-2.png 259w", "/version/2.6.2/static/200f50aebb501fdd2ff5f564825d8634/6b9fd/text-color-meaning-2.png 518w", "/version/2.6.2/static/200f50aebb501fdd2ff5f564825d8634/8ae3e/text-color-meaning-2.png 756w"],
                "sizes": "(max-width: 756px) 100vw, 756px",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                },
                "loading": "lazy"
              }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                "className": "title title-correct"
              }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`
            A message is successful if you can remove the color from it, and the
            meaning is still clear.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
  `}</div>
</div>
<h4>{`Subdued`}</h4>
<p>{`It is common to lighten certain pieces of text in order to create more contrast. For example, making a subtitle more subdued than the title, or instructional text like the helper text on form inputs. This can be easily done by applying the "subdued" color to a piece of text. On a light background, this makes the color #707070. The inverse equivalent is a semi-transparent white.`}</p>
<div {...{
      "className": "two-col"
    }}>{`
  `}<div parentName="div" {...{
        "className": "row"
      }}>{`
    `}<div parentName="div" {...{
          "className": "col col-1"
        }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
              "className": "gatsby-resp-image-wrapper",
              "style": {
                "position": "relative",
                "display": "block",
                "marginLeft": "auto",
                "marginRight": "auto",
                "maxWidth": "756px"
              }
            }}>{`
      `}<span parentName="span" {...{
                "className": "gatsby-resp-image-background-image",
                "style": {
                  "paddingBottom": "66.02316602316603%",
                  "position": "relative",
                  "bottom": "0",
                  "left": "0",
                  "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAABOklEQVQoz61Sy0rDQBTtTn+gWrGfoT/SpTboXvCj3LuQahEEKRgHqkiyUAhoEslEEtNkEvNOJpl420iVais+7uJy5gxnzrkz06gmxRibAdPlDPOxGtUfaizO89x13TRNAYRhSCmN4xgwMISQIAiKopgrtm2b53mEkCzLoigKggAdGIyxJEnAK4ryeaI3MRycJEkURWCVZRnYAgAGOs0pMGVZznWGbdM0LctyHMfzPOgwBQQ2DOPZHvm+/5sLY0FCb7UxAmfI/GXs6ZPUU70/D6soHjFa/My5yKl7LpDjITnknYMzcnpNjpA/lNhice3pGyO5taU3Odze1Zpd3OK0pY6ysZdO4nwjfjGs+3XucWVbbe+oq111jVOXOw+b+8licV00y83BDe7x+snlUx/pfYR7F9bVXfmP3/MVMhneBYGWaesAAAAASUVORK5CYII=')",
                  "backgroundSize": "cover",
                  "display": "block"
                }
              }}></span>{`
  `}<img parentName="span" {...{
                "className": "gatsby-resp-image-image",
                "alt": " ",
                "title": " ",
                "src": "/version/2.6.2/static/557bc25649b7c81fd3df0ee2692e5a7f/8ae3e/subdued-example-1.png",
                "srcSet": ["/version/2.6.2/static/557bc25649b7c81fd3df0ee2692e5a7f/a2ead/subdued-example-1.png 259w", "/version/2.6.2/static/557bc25649b7c81fd3df0ee2692e5a7f/6b9fd/subdued-example-1.png 518w", "/version/2.6.2/static/557bc25649b7c81fd3df0ee2692e5a7f/8ae3e/subdued-example-1.png 756w"],
                "sizes": "(max-width: 756px) 100vw, 756px",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                },
                "loading": "lazy"
              }}></img>{`
    `}</span>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
          "className": "col col-2"
        }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
              "className": "gatsby-resp-image-wrapper",
              "style": {
                "position": "relative",
                "display": "block",
                "marginLeft": "auto",
                "marginRight": "auto",
                "maxWidth": "756px"
              }
            }}>{`
      `}<span parentName="span" {...{
                "className": "gatsby-resp-image-background-image",
                "style": {
                  "paddingBottom": "66.02316602316603%",
                  "position": "relative",
                  "bottom": "0",
                  "left": "0",
                  "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAABI0lEQVQoz6WSsU7DMBCGzc7SnQdhQ1m6tEJiYejGMyAkUAtIbLwHA4/DgIroEBTTRImSkNhRbGPHOc7JwtKQil+nX16++31nEwBofwn2EYF/yMFZloVhGMcxpVRrvR/MGEO+KIokSZRSTSfs8r1bfQax1tZ1nec5wmVZpmnaO3axO4SrkVJiAMGTEALDEWZlic45R8eOA8kIYxd37aqqcGDG2Rdnxph+Hjuo/l0cbLXRQplKyNeg1Q00FkwDI56tg7vCGdQbNbbFaDNy24hFD0/B8eXn/J56N3S6pNNV4F1zP3T/xw7lE1y5f7bckll0dBFOFtvD82iy8A9O0pd1B9s/koPH53fvanN6t5nfupqt0NnHiGToJhTQSgDp3JWCUd/8B8Cz4QDLUezIAAAAAElFTkSuQmCC')",
                  "backgroundSize": "cover",
                  "display": "block"
                }
              }}></span>{`
  `}<img parentName="span" {...{
                "className": "gatsby-resp-image-image",
                "alt": " ",
                "title": " ",
                "src": "/version/2.6.2/static/d47b426a926669baaff4d9a6672140ba/8ae3e/subdued-example-2.png",
                "srcSet": ["/version/2.6.2/static/d47b426a926669baaff4d9a6672140ba/a2ead/subdued-example-2.png 259w", "/version/2.6.2/static/d47b426a926669baaff4d9a6672140ba/6b9fd/subdued-example-2.png 518w", "/version/2.6.2/static/d47b426a926669baaff4d9a6672140ba/8ae3e/subdued-example-2.png 756w"],
                "sizes": "(max-width: 756px) 100vw, 756px",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                },
                "loading": "lazy"
              }}></img>{`
    `}</span>{`
      `}</figure>{`
    `}</div>{`
  `}</div>
</div>
<hr></hr>
<h2>{`Margins`}</h2>
<p>{`Headings and paragraphs have margins built into them by default. These margins provide the desired rhythm created by spacing when they are used together in a traditional narrative context. These margins can be easily modified using the spacing scale or completely removed when using headings or paragraphs in other contexts.`}</p>
<figure>{`
  `}<span parentName="figure" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1035px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "101.15830115830116%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAIAAAAC64paAAAACXBIWXMAAAsTAAALEwEAmpwYAAACIElEQVQ4y52Ta2/aMBSG+///xlatY+2HaVtb0W6lhY1LuYgCcUKgKW255EbSODiJ7fjMQIZUrcA06/GRZfvVa59jH8CyRQAhiDUYUrwcyBn+Asz/m5RTzrmUHQghXGvomsieobml+Y4ezIcyuqZKyRi4Dcx6jZ3ymFKWiR81pVMpqY1ap1zSW/Untac2qs3i1cKaAAkE9iD0X7GyXYoBBNxdQukIysfwMwe/clD6CDcfoHgkCofi6v1r3kHhkLnTaO0s+/h+8KB0hr321BgYqDM1dCA4XQSCYHiTNN04Qziqeujc0/Iv+qWLzmUEswaz6tuYNR75cUxXYiGgWxTlz3B7KipfoPoVat/kYBtyA/PthPHM+Xmk9JWqoTd1VBtq9emTAmy+A1kq9kcsQPsOjZxonkDrBJrHovFpOzlonlBvtojirFTOgz5R22a/a/Y7zJnAiw2+tQPBaCovuz52MqpE6Ixo+bh/IR4KMLrexeNNgp1wQTJnb9Cy7q49VLa7Rfp0B6YKM7QVU+URTijNnKlRibqnCbqIUT7Vf4jh1Q7gvkCxS9Z3lt0d3LpKwUE3eFgBqwdmdxdWj5GAbBLmWrpvq66pMPIM3AQ22438GFmppDi0e8GkFkzraaBAqO1h0ecJThKaiclcDc0mcdpitbYHonMaZl9SPk8snWe3LEDLtX9xpmGcZNkWC88IHZViA5IxxM97SMZJjKNNtv+7/QbU8FD6m2ht4QAAAABJRU5ErkJggg==')",
            "backgroundSize": "cover",
            "display": "block"
          }
        }}></span>{`
  `}<img parentName="span" {...{
          "className": "gatsby-resp-image-image",
          "alt": " ",
          "title": " ",
          "src": "/version/2.6.2/static/74f59b7499366e449578e9bd1578a0eb/e3189/margins-illustration.png",
          "srcSet": ["/version/2.6.2/static/74f59b7499366e449578e9bd1578a0eb/a2ead/margins-illustration.png 259w", "/version/2.6.2/static/74f59b7499366e449578e9bd1578a0eb/6b9fd/margins-illustration.png 518w", "/version/2.6.2/static/74f59b7499366e449578e9bd1578a0eb/e3189/margins-illustration.png 1035w", "/version/2.6.2/static/74f59b7499366e449578e9bd1578a0eb/f793b/margins-illustration.png 1404w"],
          "sizes": "(max-width: 1035px) 100vw, 1035px",
          "style": {
            "width": "100%",
            "height": "100%",
            "margin": "0",
            "verticalAlign": "middle",
            "position": "absolute",
            "top": "0",
            "left": "0"
          },
          "loading": "lazy"
        }}></img>{`
    `}</span>
</figure>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      